/**
 * Admin Quick Start Guide
 *
 * Daily workflow guide for administrators showing what to do and when.
 * Part of the DOC-06 role-specific quick start guides.
 */

export const adminQuickStartContent = {
  id: 'quickstart-admin',
  title: 'Admin Quick Start Guide',
  category: 'quickstart',
  roles: ['admin'],
  keywords: [
    'getting started', 'admin', 'setup', 'configuration', 'first steps',
    'how to start', 'new admin', 'onboarding', 'tutorial', 'daily workflow'
  ],

  sections: [
    {
      title: 'Welcome, Admin!',
      content: `As an admin, you oversee all gym operations and have access to every feature in the WTF Sales Tracker.

**Your main responsibilities:**
- Monitor performance across all gyms via the Command Center
- Verify pulse entries submitted by managers
- Manage notification rules and settings
- Review activity logs for auditing and troubleshooting`
    },
    {
      title: 'Your Morning Routine (8:00-9:00 AM)',
      content: `Start your day by getting a quick picture of yesterday's performance and any overnight issues.

**Step 1: Check Command Center**
- **Go to:** Command Center (first page after login)
- **Look for:** Red badges on gyms indicating problems, fill rates below 100%
- **Action:** Note which gyms need attention today

**Step 2: Review Overnight Alerts**
- **Go to:** Alerts page
- **Look for:** Critical (red) alerts from yesterday evening
- **Action:** Address any urgent issues that occurred after hours`
    },
    {
      title: 'Throughout the Day',
      content: `Monitor pulse submissions and verify entries as they come in during each pulse period.

**After Each Pulse Period (P1, P2, P3, P4):**

| Pulse | Time Window | Check After |
|-------|-------------|-------------|
| P1 | 8:00-12:00 | 12:15 PM |
| P2 | 12:00-16:00 | 4:15 PM |
| P3 | 16:00-20:00 | 8:15 PM |
| P4 | 20:00-22:00 | 10:15 PM |

**Monitor Pulse Operations:**
- **Go to:** Pulse Operations
- **Check:** Summary cards for overall fill rates and metrics
- **Action:** Click any card to drill down into gym-level details

**Verify Entries:**
- **Go to:** Verification page
- **Look for:** Unverified entries (TL verified, needs manager approval)
- **Action:** Review and verify entries from your managers

*Tip: Address low fill rates immediately - the sooner you follow up, the more time TLs have to submit.*`
    },
    {
      title: 'End of Day (After P4)',
      content: `Wrap up the day by reviewing overall performance and following up on commitments.

**Review Daily Performance:**
- **Go to:** Command Center
- **Check:** Final fill rates across all gyms (should be at 100%)
- **Action:** Note any gyms that underperformed for tomorrow's follow-up

**Check Commitments:**
- **Go to:** Commitment Dashboard
- **Review:** Unfulfilled commitments - which gyms have pending closures?
- **Action:** Follow up with team leaders who have overdue commitments

**Review Alerts:**
- **Go to:** Alerts page
- **Check:** All critical and warning alerts for the day
- **Action:** Ensure all high-priority issues have been addressed`
    },
    {
      title: 'Weekly/As-Needed Tasks',
      content: `These tasks don't need to happen daily but should be reviewed regularly.

**Review Activity Logs (Weekly):**
- **Go to:** Activity Logs
- **Filter:** By date range for the past week
- **Look for:** Unusual patterns, failed notifications, or suspicious activity
- **Action:** Investigate any anomalies

**Manage Notification Rules (As Needed):**
- **Go to:** Rules page
- **Review:** Rule effectiveness - are rules triggering as expected?
- **Action:** Create new rules for recurring issues, disable ineffective rules

**Check Notification System (Weekly):**
- **Go to:** Notifications page
- **Verify:** All jobs are enabled and running on schedule
- **Check:** Look for failed notifications in the job history
- **Action:** Re-enable any disabled jobs, troubleshoot failures

**User Management (As Needed):**
- Add new team leaders and managers
- Update role assignments
- Review user activity for compliance`
    },
    {
      title: 'Quick Links',
      links: [
        { label: 'Command Center', path: '/', description: 'Overview of all gym performance' },
        { label: 'Verification', path: '/verification', description: 'Review and verify pulse entries' },
        { label: 'Pulse Operations', path: '/pulse-operations', description: 'Monitor pulse submission rates' },
        { label: 'Commitment Dashboard', path: '/commitment-dashboard', description: 'Track closure commitments' },
        { label: 'Rules', path: '/rules', description: 'Manage notification rules' },
        { label: 'Activity Logs', path: '/activity-logs', description: 'Audit system activity' },
        { label: 'Notifications', path: '/notifications', description: 'Manage notification jobs' },
        { label: 'Alerts', path: '/alerts', description: 'View active alerts' }
      ]
    },
    {
      title: 'Pro Tips',
      content: `**Set up your workflow for success:**

1. **Bookmark the Command Center** - Make it your browser homepage for quick morning checks

2. **Enable browser notifications** - Get desktop alerts when critical issues occur

3. **Create rules for common problems** - If you find yourself checking the same things repeatedly, create a rule to automate the notification

4. **Use the drill-down feature** - Don't just look at summary numbers; click through to see gym-level details

5. **Check Activity Logs when troubleshooting** - When something seems wrong, the logs show exactly what happened and when

**Common admin questions:**
- "Why didn't I get a notification?" - Check the Notifications page for job status and the Activity Logs for delivery attempts
- "How do I see who verified what?" - Activity Logs filter by action type "verification"
- "Can I create a rule for X?" - The Rules page supports conditions on any pulse field, time-based triggers, and multi-channel notifications`
    }
  ]
};

export default adminQuickStartContent;
